import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Loader } from 'Components';
import { Box } from '@mui/material';
import { RequestsContext } from 'Context';
import { IFormMetaData, createRequest, getRequestFields } from 'API';
import { NoteTextField } from './NoteTextField';
import { SuccessDialog } from '../SuccessDialog';
import {
  useAppSelector,
  useDictionary,
  useDisabilityCertAccess,
  useDispatchApp,
  useImposter,
  useIsDesktop,
} from 'Hooks';
import { ActionButtons } from './ActionButtons';
import { dataMapper } from './Utils';
import { FileUploadArea } from './FileUploadArea';
import { AddUploadButton } from './AddUploadButton';
import { InfoBox } from './InfoBox';
import { FileAttachmentTypography } from './FileAttachmentTitle';
import { getPersonalDetails } from 'API/personalDetails';
import { setPersonalDetails } from 'Store/PersonalDetailsSlice';
import { PersonalAreaRecommendation } from './PersonalAreaRecommendation';
import { useNavigate } from 'react-router-dom';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { SubCategories } from 'Modules/Suppliers/SuppliersMain/ResultsPages/Header/DynamicAdvancedCriteriaFilter/CriteriaFilter/SubCategories';

export interface IError {
  isTypeError: boolean;
  isFileError: boolean;
}

export interface IFileUpload {
  parentId: number | null;
  subId: number;
  goBack: () => void;
  handleSuccess: () => void;
  resetRequest: () => void;
  titleLabel: string;
  onlineFormLink?: string;
  onlineFormLinkPrefixText?: string;
  onlineFormLinkText?: string;
  isDownload?: boolean;
  isOtp: boolean;
}

const personalRecommendationUrls: { [key: number]: string } = {
  200: `/requests/update-personal-details/8/200`,
  201: `/requests/update-bank-details/8/201`,
  202: `/requests/expense-claim/3/202`,
  203: `/requests/expense-claim/3/203`,
  204: `/requests/expense-claim/3/204`,
  205: `/requests/expense-claim/3/205`,
  206: `/requests/expense-claim/3/206`,
  207: `/requests/expense-claim/3/207`,
};
const approvalRecommendations = [7, 17, 68];

const ironSwordsFormId = 300;

const disabledVeteranSubId = 23;

export const FileUpload = forwardRef<HTMLButtonElement, IFileUpload>(
  (
    {
      goBack,
      parentId,
      subId,
      handleSuccess,
      resetRequest,
      titleLabel,
      onlineFormLink,
      onlineFormLinkPrefixText,
      onlineFormLinkText,
      isDownload,
      isOtp,
    }: IFileUpload,
    ref
  ) => {
    const { requestsData, setRequestsData } = useContext(RequestsContext);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [uploadFilesList, setUploadFilesList] = useState<IFormMetaData>();
    const [note, setNote] = useState('');
    const [internetReqNum, setInternetReqNum] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isNoteError, setIsNoteError] = useState<boolean>(false);
    const isDesktop = useIsDesktop();
    const [errors, setErrors] = useState<IError[]>([]);

    const { imposterId, fakeUser } = useImposter();
    const { personalDetails } = useAppSelector(
      ({ personalDetails }) => personalDetails
    );

    const getUploadFileList = useCallback(async () => {
      if (subId) {
        const getFields = await getRequestFields(subId);
        if (getFields) {
          setUploadFilesList(getFields);
        }
      }
    }, [subId]);

    const focusElementOrFirstFocusableChild = (element: HTMLElement | null) => {
      if (!element) return;

      if (element.tabIndex >= 0 && !element.id.includes('upload-file-')) {
        element.focus();
        return;
      }

      const focusableElements = element.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstFocusableElement = Array.from(focusableElements).find(
        (el) => !el.id.includes('upload-file-')
      );

      if (firstFocusableElement) {
        firstFocusableElement.focus();
      }
    };

    const validateFormSubmitHandler = () => {
      const isNoteError = note.length === 0;
      const newErrors: IError[] = requestsData.fieldAttachmentsData.map(
        (field) => ({
          isFileError: Object.keys(field.attachments).length === 0,
          isTypeError: !field.fieldId,
        })
      );

      setErrors(newErrors);
      if (isNoteError) {
        setIsNoteError(true);
      }
      if (
        isNoteError ||
        newErrors.find(
          (error: IError) => error.isFileError || error.isTypeError
        )
      ) {
        setIsPageLoading(false);

        setTimeout(() => {
          focusElementOrFirstFocusableChild(
            document.querySelector('[aria-invalid="true"]')
          );
        }, 0);
        return;
      }
      setRequestsData((prevRequestData) => {
        const clonePrevRequestData = { ...prevRequestData };
        clonePrevRequestData.note = note;
        clonePrevRequestData.type = subId;

        return clonePrevRequestData;
      });

      return true;
    };

    const dispatch = useDispatchApp();

    const otpData = useAppSelector(
      ({ otpLoginDetails: { otpLoginDetails } }) => otpLoginDetails
    );

    const initPersonalDetails = useCallback(async () => {
      setIsPageLoading(true);
      if (!uploadFilesList) getUploadFileList();

      if (!otpData) {
        const result = await getPersonalDetails();
        if (result) dispatch(setPersonalDetails(result));
      }
      setIsPageLoading(false);
    }, [dispatch, getUploadFileList, otpData, uploadFilesList]);

    useEffect(() => {
      window.scroll(0, 0);
      initPersonalDetails();
    }, [initPersonalDetails, imposterId]);

    const handleSubmit = async () => {
      setIsPageLoading(true);

      if (validateFormSubmitHandler()) {
        const response = await createRequest(
          dataMapper(
            { ...requestsData, note: note, type: subId },
            subId,
            otpData || personalDetails
          )
        );
        setIsPageLoading(false);
        if (response && response.data?.internetReqNum) {
          setInternetReqNum(response.data.internetReqNum);
          setIsDialogOpen(true);
          handleSuccess();
        } else {
          navigate('/error');
        }
      }
    };

    const addNewUploadArea = () => {
      if (
        requestsData.fieldAttachmentsData.find(
          (field) =>
            Object.keys(field.attachments).length === 0 || field.fieldId === ''
        ) === undefined
      ) {
        const newFieldAttachmentsData = [...requestsData.fieldAttachmentsData];
        newFieldAttachmentsData.push({
          fieldId: '',
          attachments: {},
        });
        setErrors((prevErrors) => [
          ...prevErrors,
          { isFileError: false, isTypeError: false },
        ]);

        setRequestsData((prevRequestsData) => ({
          ...prevRequestsData,
          fieldAttachmentsData: newFieldAttachmentsData,
        }));
        setTimeout(() => {
          const ariaUploadsElements = Array.from(
            document.querySelectorAll('.uploadBox')
          );
          const lastAriaUploadElement = ariaUploadsElements?.slice(-1)[0] as
            | HTMLElement
            | undefined;
          lastAriaUploadElement?.focus();
        }, 0);
      }
    };

    const onSetNote = (value: string) => {
      if (isNoteError && value.length > 0) {
        setIsNoteError(false);
      }
      setNote(value);
    };

    const navigate = useNavigate();

    const { hasAccess: hasVeteranDisabilityCert } = useDisabilityCertAccess();

    const shouldShowRecommendation = useCallback(() => {
      if (
        !isOtp &&
        subId === disabledVeteranSubId &&
        !hasVeteranDisabilityCert
      ) {
        return false;
      }
      return !!onlineFormLink && onlineFormLink !== '';
    }, [onlineFormLink, hasVeteranDisabilityCert, isOtp, subId]);

    const isApprovalRecommendation = () => {
      return !!approvalRecommendations.find((id) => id === subId);
    };

    const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);

    useEffect(() => {
      if (subId === ironSwordsFormId)
        setNote(dictionary.uploadFile.inputFor300);
    }, [dictionary.uploadFile.inputFor300, subId]);

    return (
      <>
        {isPageLoading ? (
          <Loader />
        ) : isDialogOpen ? (
          <SuccessDialog
            title={titleLabel}
            userData={otpData || personalDetails}
            reqNum={internetReqNum}
            resetRequest={resetRequest}
          />
        ) : (
          <>
            {shouldShowRecommendation() && (
              <PersonalAreaRecommendation
                description={
                  onlineFormLinkPrefixText ||
                  (isApprovalRecommendation()
                    ? dictionary.approvalRecommendationDescription
                    : undefined)
                }
                linkLabel={
                  onlineFormLinkText ||
                  (isApprovalRecommendation()
                    ? dictionary.approvalRecommendationLinkLabel
                    : undefined)
                }
                linkUrl={onlineFormLink || personalRecommendationUrls[subId]}
                isDownload={isDownload}
                isDisabledVeteran={
                  subId === disabledVeteranSubId &&
                  !isOtp &&
                  hasVeteranDisabilityCert
                }
              />
            )}
            <Box
              sx={
                isDesktop
                  ? {
                      width: '680px',
                      borderRadius: '20px',
                      bgcolor: 'primary.shade_100',
                      border: '1px solid',
                      borderColor: 'primary.shade_200',
                      boxShadow: '0px 2px 16px rgba(8, 30, 84, 0.1)',
                      padding: '40px',
                      margin: '60px auto',
                    }
                  : { padding: '32px 24px' }
              }
            >
              <NoteTextField
                value={note}
                onChange={onSetNote}
                error={isNoteError}
                title={
                  subId === ironSwordsFormId
                    ? dictionary.uploadFile.noteFor300
                    : undefined
                }
              />
              <FileAttachmentTypography
                isAttached={requestsData.fieldAttachmentsData.length > 0}
              />
              {requestsData.fieldAttachmentsData.map(
                (fieldAttachmentsData, index) => (
                  <FileUploadArea
                    key={index}
                    uploadFilesList={uploadFilesList}
                    fieldAttachmentsData={fieldAttachmentsData}
                    index={index}
                    errors={
                      errors[index] || {
                        isTypeError: false,
                        isFileError: false,
                      }
                    }
                    setErrors={(error: IError) =>
                      setErrors((prevErrors) => {
                        prevErrors[index] = error;
                        return [...prevErrors];
                      })
                    }
                  />
                )
              )}
              {requestsData.fieldAttachmentsData.length === 0 && (
                <AddUploadButton
                  onClick={() => addNewUploadArea()}
                  isFirstFile
                />
              )}
              {requestsData.fieldAttachmentsData.length > 0 &&
                !requestsData.fieldAttachmentsData
                  .map((field) => ({
                    isFileError: Object.keys(field.attachments).length === 0,
                    isTypeError: !field.fieldId,
                  }))
                  .find(
                    (error: IError) => error.isFileError || error.isTypeError
                  ) && (
                  <AddUploadButton
                    onClick={() => addNewUploadArea()}
                    isFirstFile={false}
                  />
                )}
              <InfoBox />
              <ActionButtons handleBack={goBack} handleSubmit={handleSubmit} />
            </Box>
          </>
        )}
      </>
    );
  }
);
